import React, {useEffect, useState} from "react";
import {AutoComplete, Button, DatePicker, Empty, Input, Modal, notification, Select, Spin} from "antd";
import {useAuth} from "oidc-react";
import dayjs from "dayjs";
import {AddOrUpdateSessionObject} from "../../../utils/storage.utils";
import {Search} from "../../../constants/session.constants";
import SharedService from "../../../services/shared/service";

const Label = ({children}) => (
    <div className="text-[12px] font-semibold text-[#7A849A] uppercase tracking-wide mb-1.5">
        {children}
    </div>
);

const Popup = ({visible, onClose, inputValues, setInputValues, fetchInvoiceSearchData}) => {
    const {userData} = useAuth();
    const token = userData?.access_token;

    const [supplierSuggestions, setSupplierSuggestions] = useState([]);
    const [supplier, setSupplier] = useState(null);
    const [customerOptions, setCustomerOptions] = useState([]);
    const [dateRange, setDateRange] = useState([]);
    const [customerLoading, setCustomerLoading] = useState(false);
    const [supplierLoading, setSupplierLoading] = useState(false);

    const handleInputChange = (field, value) => {
        setInputValues((prev) => ({...prev, [field]: value}));
    };

    useEffect(() => {
        if (
            inputValues &&
            (inputValues.fromDate !== "" || !isNaN(inputValues.fromDate)) &&
            (inputValues.toDate !== "" || !isNaN(inputValues.toDate))
        ) {
            setDateRange([
                dayjs(inputValues.fromDate, "DD/MM/YYYY"),
                dayjs(inputValues.toDate, "DD/MM/YYYY"),
            ]);
        }

        if (isNaN(inputValues.fromDate) || isNaN(inputValues.toDate)) {
            setDateRange([]);
        }

        if (supplierSuggestions.length > 0 && inputValues.supplierId) {
            const match = supplierSuggestions.find((s) => s.id === inputValues.supplierId);
            setSupplier(match);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (inputValues.fromDate === "" || inputValues.toDate === "") {
            setDateRange([]);
        }
    }, [inputValues]);

    const fetchCustomerOptions = async (searchText) => {
        setCustomerLoading(true);
        setCustomerOptions([]);
        try {
            const response = await SharedService.post({
                token,
                url: "customer/search/autocomplete",
                data: {searchTerm: searchText},
                params: {select: "id,displayName,name,accountingType,vesselId,vesselName"},
            });

            const options = (response ?? []).map((item) => ({
                label: `${item.displayName}${item?.vesselName ? ` - (${item.vesselName})` : ""}`,
                value: `${item.displayName}${item?.vesselName ? ` - (${item.vesselName})` : ""}`,
                VesselId: item.vesselId,
                id: item.id,
                VesselName: item?.vesselName,
                customerName: item.name,
                CoyId: item.id,
                AccountingType: item.accountingType,
                consolidateDetailLines: item.consolidateDetailLines,
            }));
            setCustomerOptions(options);
        } catch (error) {
            if (error?.response?.status === 500) {
                notification.error({message: "Error", description: error.response.statusText});
            }
        } finally {
            setCustomerLoading(false);
        }
    };

    const fetchSupplierSuggestions = async () => {
        setSupplierLoading(true);
        setSupplierSuggestions([]);

        if (inputValues.supplierId?.length >= 3) {
            try {
                const response = await SharedService.post({
                    token,
                    url: "supplier/search/autocomplete",
                    data: {searchTerm: inputValues.supplierId},
                    params: {select: "id,name"},
                });
                const suggestions = (response ?? []).map((item) => ({
                    value: item.name,
                    id: item.id,
                }));
                setSupplierSuggestions(suggestions);
            } catch (error) {
                if (error?.response?.status === 500) {
                    notification.error({message: "Error", description: error.response.statusText});
                }
            } finally {
                setSupplierLoading(false);
            }
        } else {
            setSupplierLoading(false);
        }
    };

    useEffect(() => {
        fetchSupplierSuggestions();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [inputValues.supplierId]);

    const updateInputData = (data) => {
        if (!data) return;
        Object.entries(data).forEach(([field, value]) => handleInputChange(field, value));
        const updatedValues = {...inputValues, ...data};
        AddOrUpdateSessionObject(Search.InvoiceListing, updatedValues);
        setInputValues(updatedValues);
    };

    const handleSearch = () => {
        const [startDate, endDate] = dateRange || [];
        const selectedValues = Object.entries(inputValues).reduce((acc, [key, value]) => {
            if (key !== "selectedItems" && value !== "" && value !== null) acc[key] = value;
            return acc;
        }, {});

        const data = {
            ...selectedValues,
            ...(startDate && endDate && {
                fromDate: startDate.format("DD-MM-YYYY"),
                toDate: endDate.format("DD-MM-YYYY"),
            }),
        };

        const updatedValues = {...inputValues, ...data, selectedItems: [data]};
        setInputValues(updatedValues);
        AddOrUpdateSessionObject(Search.InvoiceListing, updatedValues);
        fetchInvoiceSearchData();
        onClose();
    };

    const handleSupplierSelect = (selectedSupplier) => {
        const match = supplierSuggestions.find((item) => item.value === selectedSupplier);
        if (match) {
            setSupplier(match);
            updateInputData({supplierId: match.id});
        }
    };

    const handleDateChange = (dates, dateStrings) => {
        setDateRange(dates);
        updateInputData({fromDate: dateStrings[0], toDate: dateStrings[1]});
    };

    return (
        <Modal
            title="Advanced Search"
            open={visible}
            onCancel={onClose}
            width={560}
            closable={true}
            footer={[
                <Button key="cancel" onClick={onClose}>Cancel</Button>,
                <Button key="search" type="primary" onClick={handleSearch}>Search</Button>,
            ]}
        >
            <div className="flex flex-col gap-4">
                <div>
                    <Label>Customer</Label>
                    <Select
                        showSearch
                        suffixIcon={null}
                        popupMatchSelectWidth={false}
                        notFoundContent={
                            customerLoading ? (
                                <Spin className="custom-spin flex justify-center"/>
                            ) : customerOptions.length === 0 ? (
                                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE}/>
                            ) : null
                        }
                        optionFilterProp="children"
                        filterOption={(input, option) =>
                            (option?.label ?? "").toLowerCase().includes(input.toLowerCase()) ||
                            (option?.value ?? "").toLowerCase().includes(input.toLowerCase())
                        }
                        className="w-full"
                        options={customerOptions}
                        id="CustomerName"
                        placeholder="Search customer"
                        value={inputValues.customerId}
                        onChange={(_, item) => handleInputChange("customerId", item?.id)}
                        onSelect={(_, item) => handleInputChange("customerId", item?.id)}
                        onSearch={(searchText) => {
                            if (searchText.length > 2) {
                                fetchCustomerOptions(searchText.toLowerCase());
                            } else {
                                setCustomerOptions([]);
                            }
                        }}
                        allowClear
                    />
                </div>

                <div>
                    <Label>Sender Email</Label>
                    <Input
                        placeholder="sender@example.com"
                        value={inputValues.senderEmail}
                        onChange={(e) => handleInputChange("senderEmail", e.target.value)}
                        allowClear
                    />
                </div>

                <div>
                    <Label>Supplier</Label>
                    <AutoComplete
                        allowClear
                        popupMatchSelectWidth={300}
                        className="w-full"
                        placeholder="Search supplier"
                        value={supplier?.value}
                        onChange={(value) => handleInputChange("supplierId", value)}
                        onSearch={fetchSupplierSuggestions}
                        onSelect={handleSupplierSelect}
                        options={supplierSuggestions}
                        onClear={() => setSupplier(null)}
                        notFoundContent={
                            supplierLoading ? (
                                <Spin className="custom-spin flex justify-center"/>
                            ) : supplierSuggestions.length === 0 ? (
                                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE}/>
                            ) : null
                        }
                    />
                </div>

                <div>
                    <Label>Supplier Email</Label>
                    <Input
                        placeholder="supplier@example.com"
                        value={inputValues.supplierEmail}
                        onChange={(e) => handleInputChange("supplierEmail", e.target.value)}
                        allowClear
                    />
                </div>

                <div>
                    <Label>Invoice Number</Label>
                    <Input
                        placeholder="e.g. WF-2026-4488210"
                        value={inputValues.invoiceNumber}
                        onChange={(e) => handleInputChange("invoiceNumber", e.target.value)}
                        allowClear
                    />
                </div>

                <div>
                    <Label>Date Range</Label>
                    <DatePicker.RangePicker
                        format="DD-MM-YYYY"
                        placeholder={["Start date", "End date"]}
                        value={
                            dateRange && dateRange.length > 0
                                ? [dayjs(dateRange[0]), dayjs(dateRange[1])]
                                : undefined
                        }
                        onChange={handleDateChange}
                        className="w-full"
                    />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div>
                        <Label>Days</Label>
                        <Input
                            type="number"
                            placeholder="0"
                            value={inputValues.days}
                            onChange={(e) =>
                                handleInputChange("days", parseInt(e.target.value, 10) || "")
                            }
                        />
                    </div>
                    <div>
                        <Label>Reference Id</Label>
                        <Input
                            type="number"
                            value={inputValues.referenceId}
                            onChange={(e) =>
                                handleInputChange("referenceId", parseInt(e.target.value, 10) || "")
                            }
                        />
                    </div>
                    <div>
                        <Label>Email Queue Ref</Label>
                        <Input
                            type="number"
                            value={inputValues.emailReferenceId}
                            onChange={(e) =>
                                handleInputChange("emailReferenceId", parseInt(e.target.value, 10) || "")
                            }
                        />
                    </div>
                </div>
            </div>
        </Modal>
    );
};

export default Popup;
